// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ISafeAny = any;
export type IScrollToLabelParams = ISafeAny;

export interface ISearchParams {
  keyword: string;
  caseSensitive: boolean;
}
export interface IPdfConfig {
  pdfOnly: boolean;
  pdfUrl: string;
  pdfInfoUrl: string | IViewPort[];
  pdfCharsUrl: string | string[];
  pdfTextUrl: string;
  pdfEnablePreRender?: boolean;
  pdfPreRenderUrl?: string;
  pdfPageMaxCacheCount?: number; // 最大缓存页面数
  pdfPagePreloadCount?: number; // 预加载页面数量
  pdfLazyLoading?: boolean; // 支持 pdf 懒加载，默认为 true
  pdfGetDocumentParams?: DocumentInitParameters;
}

export interface IStyles {
  normalStyle?: Partial<CSSStyleDeclaration>;
  hoverStyle?: Partial<CSSStyleDeclaration>;
  selectedStyle?: Partial<CSSStyleDeclaration>;
  activeStyle?: Partial<CSSStyleDeclaration>;
}

export interface IFunctionsConfig {
  labelVisible: boolean;
  labelDeletable: boolean;
  labelSelectable: boolean;
  tableMergeable: boolean;
  tableSplitable: boolean;
  tableVisible: boolean;
  watchTableChange: boolean; // 监听表格 dom 变更
  defaultZoom?: number; // ready$ 后立即 setZoom 会有 pdf 渲染问题，这时可以设置 defaultZoom
  rotationList: IPdfRotation; // 与 setRotation 一致，主要用于解决旋转纠正闪烁的情形
}

export interface ITableMirroringConfig {
  tableMirroring: boolean; // 是否开启表格镜像操作（只支持新增竖线与删除竖线）
  tableMirroringPages: boolean[]; // 表格镜像操作页码数
}

export interface IPageLoadingConfig {
  pageLoadingStyle?: Partial<CSSStyleDeclaration>;
}

export interface IConfig extends IPdfConfig, Partial<IFunctionsConfig>, Partial<ITableMirroringConfig>, IPageLoadingConfig {
  id: string;
  operateMode?: OperateMode;
  requestConfig?: ISafeAny; // 与 axios 请求配置一致，https://github1s.com/axios/axios/blob/HEAD/index.d.ts#L50
}

export interface IReadyParams {
  pageCount: number;
}

export interface IViewPort {
  basePageIndex?: number;
  pageCharsCount?: number;
  height: number;
  scale: number;
  width: number;
  rotation?: number;
  num: number;
}
export interface IViewPortsObj {
  [key: number]: IViewPort;
}

export interface IMagnifierChangeEvent {
  viewportWidth: number;
  viewportHeight: number;
  top: number;
  left: number;
  width: number;
  height: number;
  pageNumber: number;
  src: string;
}

export interface IHistoryChangeEvent {
  counts: number;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  labels?: ILabels;
  tables?: ITables;
}

export type IPdfRotation = number[];

export interface IPdfScrollingEvent {
  top: number;
}

export interface IKeyBoardEvent {
  altKey: boolean;
  char: string;
  charCode: number;
  code: string;
  ctrlKey: boolean;
  isComposing: boolean;
  key: string;
  keyCode: number;
  location: number;
  metaKey: boolean;
  repeat: boolean;
  shiftKey: boolean;
  DOM_KEY_LOCATION_LEFT: number;
  DOM_KEY_LOCATION_NUMPAD: number;
  DOM_KEY_LOCATION_RIGHT: number;
  DOM_KEY_LOCATION_STANDARD: number;
}

export enum OperateMode {
  none = 'none',
  /**
   * 放大镜
   */
  magnifier = 'magnifier',
  /**
   * 划选
   */
  textSlip = 'textSlip',
  /**
   * 框选
   */
  textFrame = 'textFrame',
  /**
   * 组合
   */
  textGroup = 'textGroup',
  /**
   * 选择单元格
   */
  tableSelectCells = 'tableSelectCells',
  /**
   * 新增表格
   */
  tableCreateTables = 'tableCreateTables',
  /**
   * 调整表格
   */
  tableResizeTables = 'tableResizeTables',
  /**
   * 删除表格
   */
  tableDeleteTables = 'tableDeleteTables',
  /**
   * 增加横线
   */
  tableCreateHLines = 'tableCreateHLines',
  /**
   * 增加竖线
   */
  tableCreateVLines = 'tableCreateVLines',
  /**
   * 增加表格线 (任意长度)
   */
  tableCreateLines = 'tableCreateLines',
  /**
   * 调整线条 | 调整单元格（不规则表格）
   */
  tableResizeCells = 'tableResizeCells',
  /**
   * 删除线
   */
  tableDeleteLines = 'tableDeleteLines',
  /**
   * 删除单元格线
   */
  tableDeleteCellLines = 'tableDeleteCellLines',
  /**
   * 绘制单元格
   */
  tableCreateCells = 'tableCreateCells',
  /**
   * 表格受控模式
   */
  tableAllControl = 'tableAllControl',
  /**
   * 绘制多边形
   */
  polygonDrawing = 'polygonDrawing',
  /**
   * 绘制矩形
   */
  rectDrawing = 'rectDrawing',
  /**
   * 调整形状
   */
  shapeResizing = 'shapeResizing'
}


export interface IScrollOffset {
  offsetTop?: number;
  offsetLeft?: number;
}

export interface IScrollToRectParams extends IScrollOffset {
  pageIndex: number;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface IScrollToPageParams {
  pageIndex: number;
  top?: number
}

// LABEL
export type ILabelPlacement = 'top' | 'left' | 'right' | 'bottom' | 'topLeft' | 'topRight' |
  'bottomLeft' | 'bottomRight' | 'leftTop' | 'leftBottom' | 'rightTop' | 'rightBottom';

export interface ILabel extends IStyles {
  uuid: string;
  word: string;
  index: number;
  words?: ILabelRangeWord[];
  selected?: boolean; // 是否已选择
  active?: boolean; // 是否被激活
  pageNumber?: number; // 所在页码
  unselectable?: boolean; // 是否可被选中
  deleteable?: boolean; // 是否可删除
  position?: ILabelPosition; // 所在位置
  lastPageNumber?: number; // 所在最后一页页码（针对跨页标注）
  prevPagesHeight?: number; // 上一页高度
  activeAnimation?: 'normal' | 'flash';
  tooltipHidden?: boolean; // 是否隐藏 tooltip
  controlExp?: { // 标签（实验性特性）
    text: string;
    height: number; // height & fontSize
    style: Partial<CSSStyleDeclaration>;
    placement?: ILabelPlacement;
    visible?: 'none' | 'start' | 'end' | 'all'
  }
  info?: {
    [prop: string]: ISafeAny;
  }
}

export interface ILabelRangeWord {
  index: number; // 当前页 index
  word: string;
  pageNumber: number;
  top: number;
  left: number;
  width: number;
  height: number;
  docIndex: number; // 全文 index
}

// 搜索后结果
export interface ISearchLabel extends ILabel {
  prefix: string; // 前缀词
  suffix: string; // 后缀词
}

export type ILabelPosition = [number, number, number, number]; // [left, top, width, height]

export type ILabels = ILabel[];
export type ISearchLabels = ISearchLabel[];

export interface IClickMetadata {
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  which: number;
}

export interface IPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface IPointer {
  x: number;
  y: number;
}

export interface IEvent<T> {
  data: T[];
}

export interface IEventWithPosition<T> {
  data: T[];
  position: IPosition;
}

export type ICreateLabelsEvent = IEventWithPosition<ILabel>;
export type IDragLabelsEvent = IEvent<ILabel>;
export type IResizeLabelsEvent = IEvent<ILabel>;
export type IDeleteLabelsEvent = IEventWithPosition<ILabel>;
export type IMouseEnterLabelsEvent = IEventWithPosition<ILabel>;
export type IMouseLeaveLabelsEvent = IEvent<ILabel>;
export interface IClickLabelsEvent extends IEventWithPosition<ILabel> {
  metadata: IClickMetadata;
}

export interface IScrollToLabelsParams extends IScrollOffset {
  labels: ILabel[];
  activeIndex?: number;
}

export interface ISetLabelsParams {
  labels: ILabels;
  recordHistory?: boolean
}

/**
 * 可视化 page json
 */
export interface IVisualPageJsonParams {
  pageNumberList: number[];
  borderVisible?: boolean; // 是否显示边框, 默认显示 (true)
  borderCorrection?: boolean; // 是否纠正边框，默认不纠正 (false)
  textVisible?: boolean; // 是否显示文字，默认显示 (true)
  indexVisible?: boolean; // 是否显示索引，默认显示 (true)
}

export interface ISetShapesParams {
  shapes: IShapes
}

// 表格样式
export interface ITableStyle {
  // 线条样式
  lineStyle: {
    defaultColor: string; // 默认样式
  }
}

// TABLE
export interface ITable {
  table_id?: string;
  mask: number[][];
  cells: ICell[][];
  style?: ITableStyle;
  text_matrix: string[][];
  matrix_type: null | 'insert' | 'update' | 'delete'; // 在 json 中标记 table 状态
}

export interface ICell extends IStyles {
  x: number;
  y: number;
  height: number;
  width: number;
  page_num: number;
  tableId?: string;
  text?: string;
}

export type ITables = {
  page_number: number;
  tables: ITable[],
}[];

export interface IScrollToTablesParams extends IScrollOffset {
  tables: ITable[];
  activeIndex?: number;
}

export interface IScrollToTableCellsParams extends IScrollOffset {
  cells: ICell[];
  activeIndex?: number;
}

export interface IHighLightTableCellsParams extends IScrollOffset {
  cells: ICell[];
  activeIndex?: number;
}

export interface IHighLightTableCells extends IScrollOffset {
  cells: {
    x: number;
    y: number;
    width: number;
    height: number;
    groupId: string;
    pageIndex: number;
    style?: Partial<CSSStyleDeclaration>;
    hoverStyle?: Partial<CSSStyleDeclaration>;
  }[]
}

export interface ITableMatrix {
  tableId: string;
  type: 'update' | 'insert' | 'delete' | null;
  pageNumber: number;
  area: number[]; // [left top width height]
  lines: number[][][];
}

export interface IThinTable {
  tableId: string;
  pageIndex: number;
}

export interface IViewTable {
  tableId: string;
  height: number;
  top: number;
  containerHeight: number;
}

export type ICreateTablesEvent = IEvent<{
  id: string;
  area: IPosition;
  pageIndex: number;
}>

export type IViewTablesChangeEvent = IEvent<IViewTable>
export type IDeleteTablesEvent = IEvent<IThinTable>
export type IMouseEnterTablesEvent = IEventWithPosition<IThinTable>
export type IMouseLeaveTablesEvent = IEvent<IThinTable>
export interface IClickTablesEvent extends IEventWithPosition<IThinTable> {
  cell: { x: number, y: number, col: number, row: number, selected: boolean } | null
  metadata: IClickMetadata
}
export interface IUpdateTablesEvent {
  data: {
    tableId: string;
    cells: ICell[][];
    mask: number[][];
  }[]
}

export interface IMergeTablesEvent {
  data: {
    tableId: string;
    cells: ICell[][];
    mask: number[][];
    pageIndex: number;
  }[]
}

export type IHighLightCells = Array<{
  tableId: string;
  pageNumber: number;
  // 通过 开始位置 --> 结束位置获取高亮的单元格
  startPoint: IPosition;
  endPoint: IPosition;
  // 可以自定义单元格样式，没有则为默认样式
  style?: Partial<CSSStyleDeclaration>;
}>;

export type ISplitTablesEvent = IEvent<IThinTable>


/******************************************************************************
 *
 * 不规则表格
 *
 ******************************************************************************/

/**
 * 不规则表格 JSON
 */
export interface IIRCell {
  uuid: string;

  x: number;
  y: number;
  width: number;
  height: number;
  page_number: number; // 页码

  tooltip?: string; // 是否显示 tooltip，默认不显示，空字符串或 undefined 都不会显示 tooltip
  hidden?: boolean; // 是否隐藏，默认不隐藏(false)
  selected?: boolean; // 是否被选中，默认不选中(false)
  active?: boolean; // 是否激活，默认不激活(false)

  selectable?: boolean; // 是否可选中，默认可选中(true)
  deletable?: boolean; // 是否可删除，默认可删除(true)

  normalStyle?: Partial<CSSStyleDeclaration>;
  selectedStyle?: Partial<CSSStyleDeclaration>;
  hoverStyle?: Partial<CSSStyleDeclaration>;
  activeStyle?: Partial<CSSStyleDeclaration>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  info?: { [key: string]: any } // cell 的补充信息，你可以在这里补充 cell 的额外信息
}

export type IIRLineRow = [[number, number], [number, number]];

export type IIRCellRow = IIRCell[];

export type IIRTable = IIRTablePerPage[];

export interface IIRTableStyle extends ITableStyle {
}

export interface IIRTablePerPage {
  page_number: number;
  tables: Array<{
    table_id: string;
    cells: IIRCellRow[];
    lines: IIRLineRow[];
    style?: IIRTableStyle;
  }>
}

/**
 *
 * 不规则表格 EVENT
 *
 */
export type IUpdateIRTablesEvent = IEvent<{
  tableId: string;
  cells: IIRCellRow[];
  lines: IIRLineRow[];
}>;

/**
 * 创建单元格（绘制单元格）
 */
export type ICreateIRTableCellsEvent = IEvent<{
  area: { x: number, y: number, width: number, height: number };
  tableId: string;
  pageNumber: number;
  cells: IIRCellRow[];
  lines: IIRLineRow[];
}>

/**
 * 跳转到单元格
 */
export interface IScrollToIRTableCellsParams extends IScrollOffset {
  cells: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
    tableId: string;
    pageNumber: number;
    style: Partial<CSSStyleDeclaration>;
  }>;
}


/******************************************************************************
 *
 * 图形标注
 *
 ******************************************************************************/

/**
 * 新增图形
 */
export type ICreateShapesEvent = IEvent<(Omit<IShapePolygon, keyof IShapeBase> | Omit<IShapeRectangle, keyof IShapeBase>) & { pageNumber: number }>;

export type IShapeEventBase =
  (Omit<IShapePolygon, keyof IShapeBase>
    | Omit<IShapeRectangle, keyof IShapeBase>)
  & { pageNumber: number, uuid: string };

/**
 * 点击图形
 */
export type IClickShapesEvent = IEvent<IShapeEventBase>

/**
 * 更新图形
 */
export type IUpdateShapesEvent = IEvent<IShapeEventBase>;

/**
 * 删除图形
 */
export type IDeleteShapesEvent = IEvent<IShapeEventBase>;

/**
 * 图形 dom 更新
 */
export interface IShapeDomsChangeEvent {
  data: { [uuid: string]: boolean; }
}

export interface DocumentInitParameters {
  /**
   * - Basic authentication headers.
   */
  httpHeaders?: Object;
  /**
   * - Indicates whether or not
   * cross-site Access-Control requests should be made using credentials such
   * as cookies or authorization headers. The default is `false`.
   */
  withCredentials?: boolean;
  /**
   * - For decrypting password-protected PDFs.
   */
  password?: string;
  /**
   * - The PDF file length. It's used for progress
   * reports and range requests operations.
   */
  length?: number;
  /**
   * - Specify maximum number of bytes fetched
   * per range request. The default value is {@link DEFAULT_RANGE_CHUNK_SIZE}.
   */
  rangeChunkSize?: number;
  /**
   * - The worker that will be used for loading and
   * parsing the PDF data.
   */
  worker?: ISafeAny;
  /**
   * - Controls the logging level; the constants
   * from {@link VerbosityLevel} should be used.
   */
  verbosity?: number;
  /**
   * - The base URL of the document, used when
   * attempting to recover valid absolute URLs for annotations, and outline
   * items, that (incorrectly) only specify relative URLs.
   */
  docBaseUrl?: string;
  /**
   * - The URL where the predefined Adobe CMaps are
   * located. Include the trailing slash.
   */
  cMapUrl?: string;
  /**
   * - Specifies if the Adobe CMaps are binary
   * packed or not.
   */
  cMapPacked?: boolean;
  /**
   * - The factory that will be used when
   * reading built-in CMap files. Providing a custom factory is useful for
   * environments without Fetch API or `XMLHttpRequest` support, such as
   * Node.js. The default value is {DOMCMapReaderFactory}.
   */
  CMapReaderFactory?: Object;
  /**
   * - Reject certain promises, e.g.
   * `getOperatorList`, `getTextContent`, and `RenderTask`, when the associated
   * PDF data cannot be successfully parsed, instead of attempting to recover
   * whatever possible of the data. The default value is `false`.
   */
  stopAtErrors?: boolean;
  /**
   * - The maximum allowed image size in total
   * pixels, i.e. width * height. Images above this value will not be rendered.
   * Use -1 for no limit, which is also the default value.
   */
  maxImageSize?: number;
  /**
   * - Determines if we can evaluate strings
   * as JavaScript. Primarily used to improve performance of font rendering, and
   * when parsing PDF functions. The default value is `true`.
   */
  isEvalSupported?: boolean;
  /**
   * - By default fonts are converted to
   * OpenType fonts and loaded via `@font-face` rules. If disabled, fonts will
   * be rendered using a built-in font renderer that constructs the glyphs with
   * primitive path commands. The default value is `false`.
   */
  disableFontFace?: boolean;
  /**
   * - Include additional properties,
   * which are unused during rendering of PDF documents, when exporting the
   * parsed font data from the worker-thread. This may be useful for debugging
   * purposes (and backwards compatibility), but note that it will lead to
   * increased memory usage. The default value is `false`.
   */
  fontExtraProperties?: boolean;
  /**
   * - Specify an explicit document
   * context to create elements with and to load resources, such as fonts,
   * into. Defaults to the current document.
   */
  ownerDocument?: HTMLDocument;
  /**
   * - Disable range request loading of PDF
   * files. When enabled, and if the server supports partial content requests,
   * then the PDF will be fetched in chunks. The default value is `false`.
   */
  disableRange?: boolean;
  /**
   * - Disable streaming of PDF file data.
   * By default PDF.js attempts to load PDF files in chunks. The default value
   * is `false`.
   */
  disableStream?: boolean;
  /**
   * - Disable pre-fetching of PDF file
   * data. When range requests are enabled PDF.js will automatically keep
   * fetching more data even if it isn't needed to display the current page.
   * The default value is `false`.
   *
   * NOTE: It is also necessary to disable streaming, see above, in order for
   * disabling of pre-fetching to work correctly.
   */
  disableAutoFetch?: boolean;
  /**
   * - Enables special hooks for debugging PDF.js
   * (see `web/debugger.js`). The default value is `false`.
   */
  pdfBug?: boolean;
}

export type IShapes = IShape[];

export type IShape = IShapePolygon | IShapeRectangle;

export type IShapeDecalPlacement = 'top' | 'left' | 'right' | 'bottom'
  | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight'
  | 'leftTop' | 'leftBottom' | 'rightTop' | 'rightBottom';

export interface IShapeDecal {
  placement: IShapeDecalPlacement;
  text: string;
  style?: Partial<CSSStyleDeclaration>;
  bgColor?: string;
}

export interface IShapeBase extends IStyles {
  pageNumber: number;
  uuid: string;
  selected?: boolean; // 是否选中
  active?: boolean; // 是否被激活
  selectable?: boolean; // 是否可选中
  deleteable?: boolean; // 是否可删除
  hidden?: boolean; // 是否可见
  trackDomChange?: boolean; // 是否追踪 dom 变化
  activeAnimation?: 'flash' | 'normal'; // 动画方式
  decal?: IShapeDecal, // 图形的标识信息
  info?: {
    [key: string]: ISafeAny;
  }
}

export interface IShapePolygon extends IShapeBase {
  type: 'polygon';
  points: IPointer[];
}

export interface IShapeRectangle extends IShapeBase {
  type: 'rectangle';
  points: [IPointer, IPointer];
}

export interface IScrollToShapesParams extends IScrollOffset {
  shapes: IShapes;
  activeIndex?: number;
  topDistance?: number;
}
